import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiBell,
  FiMenu,
  FiChevronDown,
  FiUser,
  FiLogOut,
  FiCheckCircle,
  FiAlertCircle,
} from 'react-icons/fi';
import useAuthStore from '../../stores/useAuthStore';
import useApplicationStore from '../../stores/useApplicationStore';

const TopBar = ({ onMenuClick, isSidebarOpen, isMobile }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { getDashboard, calculateProgress } = useApplicationStore();

  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef(null);

  const dashboard = getDashboard();
  const progressPercentage = calculateProgress();

  // Calculate completed and pending sections
  const completedSections = [
    dashboard.bio,
    dashboard.ref,
    dashboard.olevel,
    dashboard.result,
    dashboard.pay,
    dashboard.photo,
  ].filter(Boolean).length;

  const totalRequiredSections = 6;
  const pendingSections = totalRequiredSections - completedSections;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getInitials = () => {
    if (!user) return 'U';
    const firstName = user.firstName || user.firstname || user.name?.split(' ')[0] || '';
    const lastName = user.lastName || user.lastname || user.name?.split(' ')[1] || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
  };

  const getUserName = () => {
    if (!user) return 'User';
    const first = user.firstName || user.firstname;
    const last = user.lastName || user.lastname;
    if (first) return `${first} ${last || ''}`.trim();
    return user.name || 'User';
  };

  const getUserEmail = () => {
    if (!user) return '';
    return user.email || user.emailaddress || '';
  };

  return (
    <header className={`fixed top-0 right-0 h-16 bg-white border-b border-neutral-200 z-30 transition-all duration-300 ${isMobile ? 'left-0' : isSidebarOpen ? 'left-72' : 'left-20'
      }`}>
      <div className="flex items-center justify-between h-full px-4 sm:px-6">
        {/* Left Section - Mobile Menu Button */}
        <div className="flex items-center space-x-4">
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 text-neutral-600 hover:bg-neutral-100 rounded-lg transition-all"
          >
            <FiMenu className="w-6 h-6" />
          </button>

          {/* Page Title - Hidden on small screens */}
          <div className="hidden sm:flex items-center space-x-3">
            <img src="/images/college-logo.png" alt="COE Kangere" className="h-9 w-9 object-contain" />
            <div>
              <h1 className="text-sm font-semibold text-neutral-900 leading-none">Adamu Tafawa Balewa</h1>
              <p className="text-xs text-neutral-500 leading-none mt-1">College of Education, Kangere</p>
            </div>
          </div>
        </div>

        {/* Right Section - Progress, Status, Notifications, Profile */}
        <div className="flex items-center space-x-3 sm:space-x-4">
          {/* Progress Indicator - Hidden on small screens */}
          <div className="hidden md:flex items-center space-x-3 px-3 py-1.5 bg-neutral-50 rounded-lg">
            <div className="flex flex-col">
              <span className="text-xs text-neutral-500">Progress</span>
              <div className="flex items-center space-x-2 mt-0.5">
                <div className="w-20 h-1.5 bg-neutral-200 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-primary-600"
                    initial={{ width: 0 }}
                    animate={{ width: `${progressPercentage}%` }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                  />
                </div>
                <span className="text-xs font-semibold text-neutral-900">{progressPercentage}%</span>
              </div>
            </div>
          </div>

          {/* Status Badges - Hidden on mobile */}
          <div className="hidden sm:flex items-center space-x-2">
            {/* Completed Badge */}
            <div className="flex items-center space-x-1.5 px-2.5 py-1.5 bg-success-50 rounded-lg border border-success-200">
              <FiCheckCircle className="w-3.5 h-3.5 text-success-600" />
              <span className="text-xs font-semibold text-success-700">{completedSections}</span>
              <span className="text-xs text-success-600">Done</span>
            </div>

            {/* Pending Badge */}
            {pendingSections > 0 && (
              <div className="flex items-center space-x-1.5 px-2.5 py-1.5 bg-warning-50 rounded-lg border border-warning-200">
                <FiAlertCircle className="w-3.5 h-3.5 text-warning-600" />
                <span className="text-xs font-semibold text-warning-700">{pendingSections}</span>
                <span className="text-xs text-warning-600">Pending</span>
              </div>
            )}
          </div>

          {/* Notifications */}
          <button className="relative p-2 text-neutral-600 hover:bg-neutral-100 rounded-lg transition-all">
            <FiBell className="w-5 h-5" />
            {/* Notification dot - uncomment when notifications are available */}
            {/* <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-danger-500 rounded-full"></span> */}
          </button>

          {/* Profile Dropdown */}
          <div className="relative" ref={profileRef}>
            <button
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              className="flex items-center space-x-2 px-2 sm:px-3 py-1.5 hover:bg-neutral-100 rounded-lg transition-all"
            >
              <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                {getInitials()}
              </div>
              <div className="hidden lg:block text-left">
                <div className="text-sm font-medium text-neutral-900 leading-none">
                  {getUserName()}
                </div>
                <div className="text-xs text-neutral-500 leading-none mt-1 max-w-[200px] truncate">
                  {getUserEmail() || 'Applicant'}
                </div>
              </div>
              <FiChevronDown
                className={`w-4 h-4 text-neutral-500 transition-transform ${isProfileOpen ? 'rotate-180' : ''
                  }`}
              />
            </button>

            {/* Dropdown Menu */}
            <AnimatePresence>
              {isProfileOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-neutral-200 overflow-hidden"
                >
                  <div className="px-4 py-3 bg-neutral-50 border-b border-neutral-200">
                    <div className="text-sm font-semibold text-neutral-900">{getUserName()}</div>
                    <div className="text-xs text-neutral-500 mt-1">
                      {getUserEmail() || 'Applicant'}
                    </div>
                  </div>

                  <div className="py-2">
                    <button
                      onClick={() => {
                        navigate('/app/bio');
                        setIsProfileOpen(false);
                      }}
                      className="w-full flex items-center space-x-3 px-4 py-2.5 text-neutral-700 hover:bg-neutral-50 transition-colors"
                    >
                      <FiUser className="w-4 h-4" />
                      <span className="text-sm font-medium">Profile</span>
                    </button>
                  </div>

                  <div className="border-t border-neutral-200">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center space-x-3 px-4 py-2.5 text-danger-600 hover:bg-danger-50 transition-colors"
                    >
                      <FiLogOut className="w-4 h-4" />
                      <span className="text-sm font-medium">Sign Out</span>
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </header>
  );
};

export default TopBar;
